import { useCallback, useEffect, useRef, useState } from 'react';
import { useChat } from '../../contexts/ChatContext';
import useMessageStore from '../../stores/messageStore';
import { useUserStore } from '../../stores/userStore';
import useContactStore from '../../stores/contactStore';

/**
 * Loads and polls messages for the tenant conversations (direct and
 * broadcast) into ChatContext. Moved out of the old UnifiedChat component
 * unchanged; the calls, ordering and polling interval are the same as before.
 * `isActiveChat` is true while the chat panel is open.
 */
export function useTenantMessageSync(isActiveChat: boolean) {
  const {
    addMessageLegacy,
    clearMessages,
    clearMessagesLegacy,
    chatType: currentChatType,
    addTenantConversation,
    activeTenantId,
  } = useChat();
  const { currentUser } = useUserStore();
  const { fetchBroadcastMessages } = useMessageStore();
  const { contacts, fetchContacts } = useContactStore();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const createdConversationsRef = useRef<Set<string>>(new Set());
  const processedMessageIdsRef = useRef<Set<number>>(new Set());

  // Fetch broadcast messages when chat opens and user is available
  useEffect(() => {
    const loadBroadcastMessages = async () => {
      if (currentUser?.organization_id && isActiveChat) {
        try {
          const broadcastMsgs = await fetchBroadcastMessages(currentUser.organization_id);
          
          // Process broadcast messages to create conversations for tenants who sent them
          if (broadcastMsgs && Array.isArray(broadcastMsgs)) {
            for (const msg of broadcastMsgs) {
              // Skip if already processed
              if (processedMessageIdsRef.current.has(msg.id)) continue;
              
              // Only process messages from tenants (not from landlords/agents)
              if (msg.sender_id && msg.sender_id !== currentUser.id) {
                // Get tenant info from sender
                let tenantId: string | undefined;
                let tenantName: string | undefined;
                
                if (msg.sender?.contact && 'id' in msg.sender.contact) {
                  tenantId = (msg.sender.contact.id as number)?.toString();
                  tenantName = `${msg.sender.contact.first_name || ''} ${msg.sender.contact.last_name || ''}`.trim() || msg.sender.username;
                } else if (msg.sender_id) {
                  const { users, fetchUsers } = useUserStore.getState();
                  let usersList = users;
                  if (users.length === 0) {
                    await fetchUsers();
                    usersList = useUserStore.getState().users;
                  }
                  
                  const senderUser = usersList.find((u: any) => u.id === msg.sender_id);
                  if (senderUser?.contact_id) {
                    tenantId = senderUser.contact_id.toString();
                    const contact = contacts.find((c: any) => c.id === senderUser.contact_id);
                    tenantName = contact 
                      ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
                      : msg.sender?.username || `Tenant ${tenantId}`;
                  }
                }
                
                if (tenantId) {
                  const conversationKey = `tenant:${tenantId}`;
                  
                  // Create tenant conversation if it doesn't exist
                  if (!createdConversationsRef.current.has(conversationKey)) {
                    addTenantConversation(tenantId, tenantName || `Tenant ${tenantId}`);
                    createdConversationsRef.current.add(conversationKey);
                  }
                  
                  // Add message to chat context as tenant message
                  addMessageLegacy({
                    role: 'tenant',
                    content: msg.content,
                    senderId: tenantId,
                  }, 'tenant', tenantId);
                  
                  processedMessageIdsRef.current.add(msg.id);
                }
              }
            }
          }
        } catch (error) {
          console.error('Failed to fetch broadcast messages:', error);
        }
      }
    };

    loadBroadcastMessages();
  }, [currentUser?.organization_id, isActiveChat, fetchBroadcastMessages, addTenantConversation, addMessageLegacy, contacts]);

  // Track which conversations we've loaded messages for
  const loadedConversationsRef = useRef<Set<string>>(new Set());
  
  // Track the last loaded conversation to detect switches
  const lastLoadedConversationRef = useRef<string | null>(null);
  
  // Load messages when switching to a tenant conversation
  useEffect(() => {
    const loadTenantMessages = async () => {
      if (!currentUser?.id || !activeTenantId || currentChatType !== 'tenant') return;
      
      const conversationKey = `tenant:${activeTenantId}`;
      
      // Clear messages when switching to a different conversation
      if (lastLoadedConversationRef.current && lastLoadedConversationRef.current !== conversationKey) {
        // Clear messages for the previous conversation
        const previousPeerId = lastLoadedConversationRef.current;
        clearMessages(previousPeerId);
        // Reset the loaded flag for the previous conversation so it can be reloaded if needed
        loadedConversationsRef.current.delete(lastLoadedConversationRef.current);
      }
      
      // Always clear messages for the NEW conversation before loading
      // This ensures we don't show messages from a different conversation
      const currentPeerId = `tenant:${activeTenantId}`;
      clearMessages(currentPeerId);
      
      // Check if we've already loaded messages for this conversation
      if (loadedConversationsRef.current.has(conversationKey)) {
        // Already loaded, just update the ref
        lastLoadedConversationRef.current = conversationKey;
        return;
      }
      
      try {
        // Extract tenant contact ID from activeTenantId
        const tenantContactId = parseInt(activeTenantId);
        if (isNaN(tenantContactId)) return;
        
        // Find the user associated with this contact
        const { users, fetchUsers } = useUserStore.getState();
        let usersList = users;
        if (users.length === 0) {
          await fetchUsers();
          usersList = useUserStore.getState().users;
        }
        
        const tenantUser = usersList.find((u: any) => u.contact_id === tenantContactId);
        if (!tenantUser) {
          console.warn(`No user found for contact ID ${tenantContactId}`);
          // Still mark as loaded to prevent repeated attempts
          loadedConversationsRef.current.add(conversationKey);
          lastLoadedConversationRef.current = conversationKey;
          return;
        }
        
        // Fetch direct messages between current user and tenant user
        const { fetchDirectMessages } = useMessageStore.getState();
        const conversationMessages = await fetchDirectMessages(tenantUser.id, 50);
        
        // Sort messages by timestamp (oldest first)
        const sortedMessages = conversationMessages.sort((a: any, b: any) => {
          const timeA = new Date(a.created_at || a.timestamp || 0).getTime();
          const timeB = new Date(b.created_at || b.timestamp || 0).getTime();
          return timeA - timeB;
        });
        
        // Add messages to chat context in chronological order
        // Use conversation-specific message tracking to avoid conflicts
        const conversationMessageIds = new Set<number>();
        
        for (const msg of sortedMessages) {
          // Track message IDs per conversation to avoid duplicates within this conversation
          if (conversationMessageIds.has(msg.id)) {
            continue;
          }
          
          // Determine if message is from tenant (incoming) or to tenant (outgoing)
          const isFromTenant = msg.sender_id === tenantUser.id;
          
          if (isFromTenant) {
            // Incoming message from tenant
            addMessageLegacy({
              role: 'tenant',
              content: msg.content,
              senderId: activeTenantId,
            }, 'tenant', activeTenantId);
          } else if (msg.sender_id === currentUser.id) {
            // Outgoing message to tenant
            addMessageLegacy({
              role: 'user',
              content: msg.content,
            }, 'tenant', activeTenantId);
          }
          
          conversationMessageIds.add(msg.id);
          // Also add to global processed set to prevent duplicate processing across conversations
          processedMessageIdsRef.current.add(msg.id);
        }
        
        // Mark this conversation as loaded
        loadedConversationsRef.current.add(conversationKey);
        lastLoadedConversationRef.current = conversationKey;
      } catch (error) {
        console.error('Failed to load tenant messages:', error);
      }
    };

    if (isActiveChat && activeTenantId && currentChatType === 'tenant') {
      loadTenantMessages();
    }
  }, [activeTenantId, currentChatType, isActiveChat, currentUser?.id, addMessageLegacy, clearMessagesLegacy]);

  // Extract message processing logic into a reusable function
  const processIncomingMessages = useCallback(async () => {
    if (!currentUser?.id) return;
    
    try {
      // Ensure users and contacts are loaded for lookups
      const { users, fetchUsers } = useUserStore.getState();
      if (users.length === 0) {
        try {
          await fetchUsers();
        } catch (err) {
          console.warn('Failed to fetch users, continuing anyway:', err);
        }
      }
      
      // Ensure contacts are loaded
      if (contacts.length === 0) {
        try {
          await fetchContacts();
        } catch (err) {
          console.warn('Failed to fetch contacts, continuing anyway:', err);
        }
      }
      
      // Fetch recent messages where current user is the receiver
      const { fetchRecentMessages } = useMessageStore.getState();
      const recentMessages = await fetchRecentMessages(50);
      
      // Process messages where current user is the receiver (incoming messages from tenants)
      // OR where current user is the sender (outgoing messages to tenants)
      for (const message of recentMessages) {
        // Skip if already processed
        if (processedMessageIdsRef.current.has(message.id)) continue;
          
          // Handle incoming messages (current user is receiver)
          if (message.receiver_id === currentUser.id) {
            // Get tenant info from sender
            let tenantId: string | undefined;
            let tenantName: string | undefined;
            
        if (message.sender?.contact && 'id' in message.sender.contact) {
              // Direct contact info available
              tenantId = (message.sender.contact.id as number)?.toString();
              tenantName = `${message.sender.contact.first_name || ''} ${message.sender.contact.last_name || ''}`.trim() || message.sender.username;
            } else if (message.sender_id) {
              // Need to find user and get their contact_id
              // First, try to get users if not already loaded
              const { users, fetchUsers } = useUserStore.getState();
              let senderUser = users.find((u: any) => u.id === message.sender_id);
              
              // If user not found, try fetching users
              if (!senderUser) {
                try {
                  await fetchUsers();
                  const updatedState = useUserStore.getState();
                  senderUser = updatedState.users.find((u: any) => u.id === message.sender_id);
                } catch (err) {
                  console.error('Failed to fetch users:', err);
                }
              }
              
              if (senderUser?.contact_id) {
                // Found user, get contact_id
                tenantId = senderUser.contact_id.toString();
                // Try to find contact name from contacts store
                const contact = contacts.find((c: any) => c.id === senderUser?.contact_id);
                tenantName = contact 
                  ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
                  : message.sender?.username || `Tenant ${tenantId}`;
              } else if (message.sender?.username) {
                // Fallback: use sender username and try to find contact by email/name
                const contact = contacts.find((c: any) => {
                  const senderEmail = (message.sender as any)?.email;
                  return (c.email && senderEmail && c.email === senderEmail) || 
                  `${c.first_name} ${c.last_name}`.trim() === message.sender?.username;
                });
                if (contact) {
                  tenantId = contact.id?.toString();
                  tenantName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
                } else {
                  // Last resort: use sender_id as tenantId
                  tenantId = message.sender_id.toString();
                  tenantName = message.sender?.username || `Tenant ${tenantId}`;
                }
              } else {
                // Last resort: use sender_id as tenantId
                tenantId = message.sender_id.toString();
                tenantName = message.sender?.username || `Tenant ${tenantId}`;
              }
            }
            
            if (tenantId) {
        const conversationKey = `tenant:${tenantId}`;
              
              // Create tenant conversation if it doesn't exist
        if (!createdConversationsRef.current.has(conversationKey)) {
                addTenantConversation(tenantId, tenantName || `Tenant ${tenantId}`);
            createdConversationsRef.current.add(conversationKey);
              }
              
              // Add message to chat context as tenant message
              addMessageLegacy({
                role: 'tenant',
                content: message.content,
                senderId: tenantId,
              }, 'tenant', tenantId);
              
              // Mark message as processed
              processedMessageIdsRef.current.add(message.id);
            }
          }
          // Handle outgoing messages (current user is sender) - add as user message
          else if (message.sender_id === currentUser.id && message.receiver_id) {
            // Get tenant info from receiver
            let tenantId: string | undefined;
            
            if (message.receiver?.contact && 'id' in message.receiver.contact) {
              tenantId = (message.receiver.contact.id as number)?.toString();
            } else if (message.receiver_id) {
              // Try to find contact from contact store
              const contact = contacts.find((c: any) => {
                // Find user with this receiver_id and get their contact_id
                const { users } = useUserStore.getState();
                const user = users.find((u: any) => u.id === message.receiver_id);
                return user && c.id === user.contact_id;
              });
              
              if (contact) {
                tenantId = contact.id?.toString();
              } else {
                tenantId = message.receiver_id.toString();
              }
            }
            
            if (tenantId) {
        const conversationKey = `tenant:${tenantId}`;
              
              // Create tenant conversation if it doesn't exist
        if (!createdConversationsRef.current.has(conversationKey)) {
                // Try to get tenant name from contact
                const contact = contacts.find((c: any) => {
                  const { users } = useUserStore.getState();
                  const user = users.find((u: any) => u.id === message.receiver_id);
                  return user && c.id === user.contact_id;
                });
                
                const tenantName = contact 
                  ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || `Tenant ${tenantId}`
                  : message.receiver?.username || `Tenant ${tenantId}`;
                
            addTenantConversation(tenantId, tenantName);
            createdConversationsRef.current.add(conversationKey);
              }
              
              // Add message to chat context as user message (sent by current user)
              addMessageLegacy({
                role: 'user',
                content: message.content,
                senderId: currentUser.id.toString(),
              }, 'tenant', tenantId);
              
              // Mark message as processed
              processedMessageIdsRef.current.add(message.id);
            }
          }
        }
      } catch (error) {
        console.error('Failed to process incoming messages:', error);
      }
    }, [currentUser?.id, addTenantConversation, addMessageLegacy, contacts, fetchContacts]);
  
  // Fetch and process incoming messages from tenants (even when chat is closed)
  useEffect(() => {
    // Process messages on mount and periodically
    processIncomingMessages();
    const interval = setInterval(processIncomingMessages, 10000); // Poll every 10 seconds
    
    return () => clearInterval(interval);
  }, [processIncomingMessages]);

  // Handle manual refresh
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await processIncomingMessages();
    } finally {
      setIsRefreshing(false);
    }
  }, [processIncomingMessages]);

  // Fetch contacts when component mounts
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  return { handleRefresh, isRefreshing, createdConversationsRef };
}
